import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  // Timeout long pour les uploads lourds (import GeoFolia, imports ZIP, etc.)
  // 5 min = large marge pour un fichier XLSX 17MB + traitement serveur 25s
  timeout: 300000,
});

// Add auth token and active societe to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add active societe ID for per-company data isolation
  const activeSocieteId = localStorage.getItem('activeSocieteId');
  if (activeSocieteId) {
    config.headers['X-Societe-Id'] = activeSocieteId;
  }
  
  return config;
});

// Helper pour enregistrer une activité dans localStorage
const logActivity = (type, title, details = '', module = '') => {
  try {
    const STORAGE_KEY = 'bureau_agricole_activities';
    const stored = localStorage.getItem(STORAGE_KEY);
    const activities = stored ? JSON.parse(stored) : [];
    
    const newActivity = {
      id: `act-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      title,
      details,
      module,
      timestamp: new Date().toISOString(),
      read: false,
    };
    
    const updated = [newActivity, ...activities].slice(0, 50);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    
    // Dispatch event pour que le contexte React se mette à jour
    window.dispatchEvent(new CustomEvent('activity-added', { detail: newActivity }));
  } catch (e) {
    console.error('Erreur enregistrement activité:', e);
  }
};

// Mapping des endpoints vers des labels lisibles
const getActionLabel = (method, url) => {
  const path = url.replace(API, '').split('?')[0];
  
  // Patterns de création
  if (method === 'post') {
    if (path.includes('/parcelles')) return { title: 'Parcelle créée', module: 'Parcellaire' };
    if (path.includes('/interventions')) return { title: 'Intervention créée', module: 'Interventions' };
    if (path.includes('/clients')) return { title: 'Client créé', module: 'CRM' };
    if (path.includes('/commandes')) return { title: 'Commande créée', module: 'CRM' };
    if (path.includes('/transactions')) return { title: 'Transaction créée', module: 'Finance' };
    if (path.includes('/documents')) return { title: 'Document créé', module: 'Réglementaire' };
    if (path.includes('/materiel')) return { title: 'Matériel créé', module: 'Matériel' };
    if (path.includes('/stocks')) return { title: 'Stock créé', module: 'Stocks' };
    if (path.includes('/equipe') || path.includes('/collaborateurs')) return { title: 'Collaborateur ajouté', module: 'Équipe' };
    if (path.includes('/factures')) return { title: 'Facture créée', module: 'Facturation' };
    if (path.includes('/devis')) return { title: 'Devis créé', module: 'Facturation' };
    return null;
  }
  
  // Patterns de mise à jour
  if (method === 'put' || method === 'patch') {
    if (path.includes('/parcelles')) return { title: 'Parcelle modifiée', module: 'Parcellaire' };
    if (path.includes('/interventions')) return { title: 'Intervention modifiée', module: 'Interventions' };
    if (path.includes('/clients')) return { title: 'Client modifié', module: 'CRM' };
    if (path.includes('/commandes')) return { title: 'Commande modifiée', module: 'CRM' };
    if (path.includes('/transactions')) return { title: 'Transaction modifiée', module: 'Finance' };
    if (path.includes('/materiel')) return { title: 'Matériel modifié', module: 'Matériel' };
    if (path.includes('/stocks')) return { title: 'Stock modifié', module: 'Stocks' };
    return null;
  }
  
  // Patterns de suppression
  if (method === 'delete') {
    if (path.includes('/parcelles')) return { title: 'Parcelle supprimée', module: 'Parcellaire' };
    if (path.includes('/interventions')) return { title: 'Intervention supprimée', module: 'Interventions' };
    if (path.includes('/clients')) return { title: 'Client supprimé', module: 'CRM' };
    if (path.includes('/materiel')) return { title: 'Matériel supprimé', module: 'Matériel' };
    return null;
  }
  
  return null;
};

// Intercepteur de réponse pour enregistrer les actions
api.interceptors.response.use(
  (response) => {
    const { method, url } = response.config;
    const label = getActionLabel(method, url);
    
    if (label) {
      // Extraire un détail du body de la réponse ou de la requête
      let details = '';
      try {
        const data = response.data;
        if (data?.nom) details = data.nom;
        else if (data?.titre) details = data.titre;
        else if (data?.description) details = data.description?.substring(0, 50);
        else if (data?.entreprise) details = data.entreprise;
        else if (response.config.data) {
          const reqData = JSON.parse(response.config.data);
          details = reqData.nom || reqData.titre || reqData.description?.substring(0, 50) || '';
        }
      } catch (e) {}
      
      const type = method === 'post' ? 'create' : method === 'delete' ? 'delete' : 'update';
      logActivity(type, label.title, details, label.module);
    }
    
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    // Enregistrer les erreurs importantes
    if (error.response?.status >= 400 && error.response?.status !== 401) {
      const { method, url } = error.config || {};
      if (method && ['post', 'put', 'patch', 'delete'].includes(method)) {
        logActivity('error', 'Erreur de sauvegarde', error.response?.data?.detail || 'Une erreur est survenue', '');
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Dashboard
export const dashboardAPI = {
  getKPIs: () => api.get('/dashboard/kpis'),
  getAlertes: () => api.get('/dashboard/alertes'),
};

// Parcellaire
export const parcellaireAPI = {
  getParcelles: () => api.get('/parcellaire/parcelles'),
  getParcelle: (id) => api.get(`/parcellaire/parcelles/${id}`),
  createParcelle: (data) => api.post('/parcellaire/parcelles', data),
  getInterventions: (parcelleId) => api.get('/parcellaire/interventions', { params: { parcelle_id: parcelleId } }),
  createIntervention: (data) => api.post('/parcellaire/interventions', data),
  getStats: () => api.get('/parcellaire/stats'),
};

// CRM
export const crmAPI = {
  getClients: (statut) => api.get('/crm/clients', { params: { statut } }),
  getClient: (id) => api.get(`/crm/clients/${id}`),
  createClient: (data) => api.post('/crm/clients', data),
  updateClient: (id, data) => api.put(`/crm/clients/${id}`, data),
  getCommandes: (statut) => api.get('/crm/commandes', { params: { statut } }),
  createCommande: (data) => api.post('/crm/commandes', data),
  getStats: () => api.get('/crm/stats'),
};

// Finance
export const financeAPI = {
  getTransactions: (type, statut) => api.get('/finance/transactions', { params: { type_transaction: type, statut } }),
  createTransaction: (data) => api.post('/finance/transactions', data),
  getTresorerie: () => api.get('/finance/tresorerie'),
  getStats: () => api.get('/finance/stats'),
};

// Reglementaire
export const reglementaireAPI = {
  getDocuments: (type, statut) => api.get('/reglementaire/documents', { params: { type_document: type, statut } }),
  createDocument: (data) => api.post('/reglementaire/documents', data),
  getIFT: () => api.get('/reglementaire/ift'),
  getStats: () => api.get('/reglementaire/stats'),
};

// AI
export const aiAPI = {
  chat: (data) => api.post('/brain/chat', data),
  getConversations: () => api.get('/brain/conversations'),
  getSuggestions: (agentType) => api.get('/brain/suggestions-habitudes', { params: { agent_type: agentType } }),
};

// CASIA - Moteur de recommandation produits naturels
export const casiaAPI = {
  // Produits
  getProduits: (famille) => api.get('/casia/produits', { params: { famille } }),
  getProduit: (id) => api.get(`/casia/produits/${id}`),
  
  // Protocoles
  getProtocoles: (culture, problematique) => api.get('/casia/protocoles', { params: { culture, problematique } }),
  getProtocole: (id) => api.get(`/casia/protocoles/${id}`),
  calculerCoutProtocole: (id, surface_ha) => api.get(`/casia/protocoles/${id}/cout`, { params: { surface_ha } }),
  
  // Recommandations
  genererRecommandation: (parcelle_id, data) => api.post(`/casia/recommandation?parcelle_id=${parcelle_id}`, data),
  getRecommandations: (parcelle_id, statut) => api.get('/casia/recommandations', { params: { parcelle_id, statut } }),
  updateStatutRecommandation: (id, statut, feedback) => api.put(`/casia/recommandations/${id}/statut`, null, { params: { statut, feedback } }),
  
  // Analyse rapide (sans parcelle)
  analyseRapide: (data) => api.post('/casia/analyse-rapide', data),
  
  // Retours terrain
  creerRetour: (data) => api.post('/casia/retours', null, { params: data }),
  getRetours: (produit_id, parcelle_id) => api.get('/casia/retours', { params: { produit_id, parcelle_id } }),
};

// Nouveau Parcellaire (GeoFolia Killer)
export const parcellaireGeoAPI = {
  // Parcelles
  getParcelles: (campagne, culture, limit = 500) => api.get('/parcellaire/parcelles', { params: { campagne, culture, limit } }),
  getParcelle: (id) => api.get(`/parcellaire/parcelles/${id}`),
  createParcelle: (data) => api.post('/parcellaire/parcelles', data),
  updateParcelle: (id, data) => api.put(`/parcellaire/parcelles/${id}`, data),
  
  // Interventions
  getInterventions: (parcelle_id, campagne, categorie) => api.get('/parcellaire/interventions', { params: { parcelle_id, campagne, categorie } }),
  getIntervention: (id) => api.get(`/parcellaire/interventions/${id}`),
  createIntervention: (data) => api.post('/parcellaire/interventions', data),
  
  // Campagnes
  getCampagnes: () => api.get('/parcellaire/campagnes'),
  createCampagne: (annee) => api.post(`/parcellaire/campagnes?annee=${annee}`),
  
  // Stats
  getStats: (campagne) => api.get('/parcellaire/stats', { params: { campagne } }),
  
  // Import GeoFolia
  importGeoFolia: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/parcellaire/import/geofolia', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getImportHistory: () => api.get('/parcellaire/import/history'),
  
  // Exports
  exportRegistrePhyto: (campagne) => api.get('/parcellaire/export/registre-phyto', { params: { campagne } }),
  exportCahierCulture: (parcelle_id, campagne) => api.get(`/parcellaire/export/cahier-culture/${parcelle_id}`, { params: { campagne } }),
  
  // IFT
  calculerIFT: (campagne, parcelle_id) => api.get('/parcellaire/ift/calcul', { params: { campagne, parcelle_id } }),
};

// Exploitation
export const exploitationAPI = {
  get: () => api.get('/exploitation'),
};

export default api;
